"use client";

import { useTranslations } from "next-intl";

/** 对比表行配置：第一列为 labelKey，第二列为 labelKeyCol1，第三列为 labelKeyCol2 */
export interface ComparisonRowConfig {
	labelKey: string;
}

const DEFAULT_ROW_KEYS: ComparisonRowConfig[] = [
	{ labelKey: "stage" },
	{ labelKey: "moldCost" },
	{ labelKey: "leadTime" },
	{ labelKey: "moldLifespan" },
	{ labelKey: "designFlexibility" },
];

export interface RapidToolingVsProductionTableProps {
	/** 翻译命名空间，需包含 title、description、keyFactorCol、col1Title、col2Title 及各行 labelKey / labelKeyCol1 / labelKeyCol2 */
	translationNamespace: string;
	/** 表格行配置，不传则用默认 5 行 */
	rowKeys?: ComparisonRowConfig[];
}

/**
 * Rapid Tooling vs Production Tooling 对比区块：奶油底、左标题+绿条、右描述、全边框居中表格
 * 样式与设计稿一致：背景 #FCFFF4，表头 Key Factor | Rapid Tooling | Production Tooling
 */
export default function RapidToolingVsProductionTable({
	translationNamespace,
	rowKeys = DEFAULT_ROW_KEYS,
}: RapidToolingVsProductionTableProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-[#FCFFF4] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题（左）+ 描述（右），顶部对齐 */}
				<div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-start">
					<div>
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<div className="mt-2 h-1 w-10 flex-shrink-0 rounded bg-themePrimary400" aria-hidden />
					</div>
					<div className="md:pt-0">
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>
				</div>

				{/* 全边框、居中对齐的对比表 */}
				<div className="mt-10 overflow-x-auto md:mt-12">
					<table className="w-full min-w-[600px] border-collapse border border-gray-200">
						<thead>
							<tr>
								<th className="border border-gray-200 bg-[#FCFFF4] p-5 text-center text-base font-bold text-black md:p-6 md:text-lg">
									{t("keyFactorCol")}
								</th>
								<th className="border border-gray-200 bg-[#FCFFF4] p-5 text-center text-base font-bold text-black md:p-6 md:text-lg">
									{t("col1Title")}
								</th>
								<th className="border border-gray-200 bg-[#FCFFF4] p-5 text-center text-base font-bold text-black md:p-6 md:text-lg">
									{t("col2Title")}
								</th>
							</tr>
						</thead>
						<tbody>
							{rowKeys.map((row) => (
								<tr key={row.labelKey}>
									<td className="border border-gray-200 bg-[#FCFFF4] p-5 text-center text-sm font-bold text-black md:p-6 md:text-base">
										{t(row.labelKey)}
									</td>
									<td className="border border-gray-200 bg-[#FCFFF4] p-5 text-center text-sm text-black md:p-6 md:text-base">
										{t(`${row.labelKey}Col1`)}
									</td>
									<td className="border border-gray-200 bg-[#FCFFF4] p-5 text-center text-sm text-black md:p-6 md:text-base">
										{t(`${row.labelKey}Col2`)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
